import React, { FunctionComponent, useState } from 'react';
import ReactQuill from 'react-quill-new';
import { UploadOutlined } from '@ant-design/icons';
import { Input, Button, PageHeader, Row, Card, Col, Form, Select, Upload } from 'antd';
import { toast } from 'react-toastify';
import DegreeService from '@services/degreeService';
import { useNavigate } from '@tanstack/react-router';
import GoalsForm from '@components/forms/GoalsForm';
import { DegreeCoursesForm } from '@components/degreeForm/DegreeCoursesForm';
import ProjectsForm from '@components/forms/ProjectsForm';
import InstructorList from '@components/instructors/InstructorList';
import { normalizeProjects } from '@components/forms/helpers';
import ApiService from '@services/apiService';
import { UploadChangeParam } from 'antd/lib/upload';

interface DegreeFormProps {
  degree: Partial<Degree>;
  courses: Course[];
}

const DegreeForm: FunctionComponent<DegreeFormProps> = ({ degree, ...rest }) => {
  const navigate = useNavigate();
  const [goals, setGoals] = useState<Goal[]>(degree.goals || []);
  const [updateLoading, setUpdateLoading] = useState(false);
  // Each course carries its kind (required/elective). Courses loaded for an
  // existing degree already carry it; default anything missing to 'required'.
  const [courses, setCourses] = useState<(Course & { kind: 'required' | 'elective' })[]>(
    (rest.courses || []).map((c) => ({
      ...c,
      kind: c.kind === 'elective' ? 'elective' : 'required',
    }))
  );
  const [projects, setProjects] = useState<Project[]>(degree.projects || []);
  const [longDescription, setLongDescription] = useState(degree.longDescription);
  const [image, setImage] = useState(degree.image);
  const [form] = Form.useForm();
  const [topicsAILessons, setTopicsAILessons] = useState<any>({
    prompt: {
      courseName: '',
      lessonInfo: '',
      lessonName: '',
      userIdentifier: '',
    },
    topics: [],
  });
  const onUploadChange = ({ file }: UploadChangeParam) => {
    if (file.percent === 100) {
      setImage(file.response);
    }
  };

  const normFile = (e: any) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e && e.fileList;
  };

  const handleSubmit = () => {
    const { validateFields } = form;

    validateFields().then((values) => {
      if (degree.id) {
        if (!values.credit || values.credit < 0) {
          values.credit = 0;
        }
        setUpdateLoading(true);

        const normalizedProjects = projects ? normalizeProjects(projects) : [];

        const savePromise =
          degree.id === -1
            ? DegreeService.create({
                ...values,
                credit: Number(values.credit),
                longDescription,
                courses,
                goals: goals as Goal[],
                projects: normalizedProjects as Project[],
                image,
              })
            : DegreeService.update(degree.id, {
                ...values,
                credit: Number(values.credit),
                longDescription,
                courses,
                id: degree.id,
                goals: goals as Goal[],
                projects: normalizedProjects as Project[],
                image,
              });

        savePromise
          .then((d) => {
            const successMessage = degree.id === -1 ? 'Create' : 'Update';

            toast.success(`${successMessage} successful!`);
            navigate({ to: `/degrees/${d.id}` });
          })
          .catch((error) => {
            const message = error.response?.data?.message;

            if (message?.includes('already exists')) {
              toast.error('Degree name already exists. Please choose another name.');
            } else {
              toast.error('Save failed!');
            }

            setUpdateLoading(false);
          });
      }
    });
  };

  return (
    <Form form={form} onFinish={handleSubmit} layout="vertical">
      <Row gutter={[16, { xs: 16, sm: 16 }]}>
        <Col lg={16} md={14}>
          <PageHeader
            style={{
              background: '#fff',
              padding: '24px 16px 16px',
            }}
            extra={[
              <Button key="save" type="primary" htmlType="submit" loading={updateLoading}>
                Save
              </Button>,
            ]}
            title={
              <>
                <h2 style={{ marginBottom: 0 }}>{degree.id === -1 ? 'Create ' : 'Edit '}Degree</h2>
              </>
            }
          />
          <div
            style={{
              background: '#fff',
              padding: '16px 16px',
            }}
          >
            <Form.Item
              name="name"
              label="Name"
              rules={[
                {
                  required: true,
                  message: 'This field is required!',
                },
              ]}
              initialValue={degree.name}
            >
              <Input />
            </Form.Item>
            <Form.Item name="introVideo" label="Intro video" initialValue={degree.introVideo}>
              <Input />
            </Form.Item>
            <Form.Item
              label="Image"
              valuePropName="fileList"
              getValueFromEvent={normFile}
              initialValue={
                image
                  ? {
                      uid: image.id,
                      name: image.url,
                      status: 'done',
                      ...image,
                    }
                  : []
              }
            >
              <>
                <div>
                  {image && image.url ? <img style={{ maxWidth: 400 }} src={image.url} /> : null}
                </div>
                <Upload
                  name="file"
                  action={ApiService.getUrl('/files')}
                  headers={ApiService.getUploadHeaders()}
                  listType="picture"
                  onChange={(e) => onUploadChange(e)}
                >
                  <Button>
                    <UploadOutlined /> Click to upload
                  </Button>
                </Upload>
              </>
            </Form.Item>
            <Form.Item
              name="type"
              label="Degree Type"
              rules={[
                {
                  required: true,
                  message: 'This field is required!',
                },
              ]}
              initialValue={degree.type}
            >
              <Select placeholder="Select degree type">
                <Select.Option value="associate">Associate Degree</Select.Option>
                <Select.Option value="bachelor">Bachelor's Degree</Select.Option>
                <Select.Option value="master">Master's Degree</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item
              name="visibility"
              label="Visibility"
              rules={[
                {
                  required: true,
                  message: 'This field is required!',
                },
              ]}
              initialValue={degree.visibility || 'public'}
            >
              <Select placeholder="Select visibility">
                <Select.Option value="public">Public</Select.Option>
                <Select.Option value="private">Private</Select.Option>
                <Select.Option value="archived">Archived</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item
              name="shortDescription"
              label="Short description"
              rules={[
                {
                  required: true,
                  message: 'This field is required!',
                },
              ]}
              initialValue={degree.shortDescription}
            >
              <Input.TextArea />
            </Form.Item>
            <div className="ant-col ant-form-item-label">
              <label className="ant-form-item-required" title="Long description">
                Long description
              </label>
            </div>
            <ReactQuill
              value={longDescription || ''}
              onChange={(value: string) => {
                setLongDescription(value);
              }}
              style={{ marginBottom: 16 }}
            />
            <Form.Item name="credit" label="Credit" initialValue={degree.credit || 0}>
              <Input type="number" min={0} />
            </Form.Item>
            <GoalsForm goals={goals} onUpdate={setGoals} />
            <DegreeCoursesForm courses={courses} setCourses={setCourses} />

            {degree.id && (
              <Card
                size="small"
                title="Instructors"
                className="sidebar-widget"
                style={{ marginBottom: 16 }}
              >
                <InstructorList type="degree" itemId={degree.id} />
              </Card>
            )}
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={updateLoading}>
                Save
              </Button>
            </Form.Item>
          </div>
        </Col>
        <Col lg={8} md={10}>
          <Card size="small" title="Capstone project" className="sidebar-widget">
            <ProjectsForm
              topicsAILessons={topicsAILessons}
              projects={projects}
              setProjects={setProjects}
            />
          </Card>
        </Col>
      </Row>
    </Form>
  );
};

export default DegreeForm;
